import { parse, format, differenceInCalendarDays, startOfDay, startOfWeek, endOfWeek, startOfMonth, startOfYear, subDays, subMonths, endOfMonth } from 'date-fns';
import { dateWithoutTimezone } from '../../lib/util';

export type FilterProps = {
  periodo?: [string, string, string];
  canal_id?: number[];
  grupo_id?: number[];
};

export const translateFilter = ({
  periodo,
  canal_id,
  grupo_id,
}: FilterProps): any => {
  const _filter: any = {};
  if (periodo) {
    const d1 = parse(periodo[1], 'yyyy-MM-dd', new Date());
    d1.setHours(0, 0, 0, 0);

    const d2 = parse(periodo[2], 'yyyy-MM-dd', new Date());
    d2.setHours(23, 59, 59, 999);

    _filter.data_criacao = [
      'between',
      [dateWithoutTimezone(d1), dateWithoutTimezone(d2)],
    ];
  }

  if (canal_id?.length) {
    _filter.canal_id = ['in', canal_id];
  }

  if (grupo_id?.length) {
    _filter.grupo_id = ['in', grupo_id];
  }

  return JSON.stringify(_filter);
};

// ---- Filtro genérico para o modal de relatórios personalizados ----

export type ReportFiltroAtivo = {
  /** Nome do campo (conforme metadados do relatório) */
  field: string;
  /** Tipo do campo: 'date', 'number', 'string' */
  type: string;
  /** Operador: 'eq', 'ne', 'gt', 'ge', 'lt', 'le', 'between', 'ilike', 'in' */
  operator: string;
  /** Valor do filtro. Para 'between' é [v1, v2]; para 'in' é string[]; demais é escalar */
  value: any;
};

/**
 * Converte um array de ReportFiltroAtivo para o JSON string aceito pelo backend
 * no parâmetro `filter` dos endpoints de relatório.
 * @param filtersMeta Metadados dos filtros (opcional). Quando fornecido, campos
 * com `valueMap` terão seus valores textuais mapeados para operador/valor real.
 */
export const translateFilterGenerico = (
  filtros: ReportFiltroAtivo[],
  filtersMeta?: Record<string, { valueMap?: Record<string, any> }>
): string => {
  const _filter: any = {};

  for (const filtro of filtros) {
    const { field, operator, value, type } = filtro;
    if (value === null || value === undefined) continue;
    if (Array.isArray(value) && value.filter((v: any) => v != null && v !== '').length === 0) continue;
    if (typeof value === 'string' && value.trim() === '') continue;

    // Campo com mapeamento textual → operador/valor real (ex: bom/normal/ruim → between range)
    const mappedEntry = filtersMeta?.[field]?.valueMap?.[value];
    if (mappedEntry !== undefined) {
      _filter[field] = mappedEntry;
      continue;
    }

    if (type === 'number' && operator === 'in' && typeof value === 'string') {
      const ids = value
        .split(/[,\n]/)
        .map(v => v.trim())
        .filter(v => v && !isNaN(Number(v)))
        .map(Number);
      if (!ids.length) continue;
      _filter[field] = [operator, ids];
    } else if (type === 'date' && operator === 'between') {
      const [v1, v2] = value as [string, string];
      if (!v1 || !v2) continue;
      const d1 = parse(v1, 'yyyy-MM-dd', new Date());
      d1.setHours(0, 0, 0, 0);
      const d2 = parse(v2, 'yyyy-MM-dd', new Date());
      d2.setHours(23, 59, 59, 999);
      _filter[field] = ['between', [dateWithoutTimezone(d1), dateWithoutTimezone(d2)]];
    } else if (type === 'date') {
      if (!value) continue;
      const d = parse(value as string, 'yyyy-MM-dd', new Date());
      if (operator === 'gt' || operator === 'ge') d.setHours(0, 0, 0, 0);
      else d.setHours(23, 59, 59, 999);
      _filter[field] = [operator, dateWithoutTimezone(d)];
    } else {
      _filter[field] = [operator, value];
    }
  }

  return JSON.stringify(_filter);
};

// ---- Opções de período relativo para relatórios Personalizados ----

export const PERIODO_RELATIVO_OPTIONS = [
  { id: 'hoje', label: 'Hoje' },
  { id: 'semana', label: 'Esta semana' },
  { id: 'mes', label: 'Este mês' },
  { id: 'ano', label: 'Este ano' },
  { id: 'ultimos_7_dias', label: 'Últimos 7 dias' },
  { id: 'ultimos_30_dias', label: 'Últimos 30 dias' },
  { id: 'ultimo_mes', label: 'Mês anterior (completo)' },
  { id: 'custom', label: 'Personalizado' },
] as const;

export type PeriodoRelativoToken = typeof PERIODO_RELATIVO_OPTIONS[number]['id'];

/**
 * Resolve um token de período relativo para datas absolutas (yyyy-MM-dd).
 * Usado ao executar relatórios Personalizados, para converter o token em datas reais.
 */
export function resolvePeriodoRelativo(
  periodo: string,
  periodo_inicio?: string | null,
  periodo_fim?: string | null,
): { dataInicio: string; dataFim: string } {
  const hoje = new Date();
  const fmt = (d: Date) => format(d, 'yyyy-MM-dd');

  switch (periodo as PeriodoRelativoToken) {
    case 'hoje':
      return { dataInicio: fmt(hoje), dataFim: fmt(hoje) };
    case 'semana':
      return { dataInicio: fmt(startOfWeek(hoje, { weekStartsOn: 0 })), dataFim: fmt(hoje) };
    case 'mes':
      return { dataInicio: fmt(startOfMonth(hoje)), dataFim: fmt(hoje) };
    case 'ano':
      return { dataInicio: fmt(startOfYear(hoje)), dataFim: fmt(hoje) };
    case 'ultimos_7_dias':
      return { dataInicio: fmt(subDays(hoje, 6)), dataFim: fmt(hoje) };
    case 'ultimos_30_dias':
      return { dataInicio: fmt(subDays(hoje, 29)), dataFim: fmt(hoje) };
    case 'ultimo_mes': {
      const inicioUltimoMes = startOfMonth(subMonths(hoje, 1));
      const fimUltimoMes = endOfMonth(inicioUltimoMes);
      return { dataInicio: fmt(inicioUltimoMes), dataFim: fmt(fimUltimoMes) };
    }
    case 'custom':
      return {
        dataInicio: periodo_inicio ?? fmt(startOfMonth(hoje)),
        dataFim: periodo_fim ?? fmt(hoje),
      };
    default:
      return { dataInicio: fmt(startOfMonth(hoje)), dataFim: fmt(hoje) };
  }
}

/**
 * Janela imediatamente anterior à informada, com a mesma duração em dias.
 *
 * Genérico de propósito: "Este mês" no dia 18 compara com os 18 dias anteriores,
 * e não com o mês-calendário inteiro — comparar 18 dias contra 31 distorceria
 * qualquer métrica de volume. Mesma regra vale para período personalizado.
 */
export function periodoAnterior(
  dataInicio: string,
  dataFim: string,
): { dataInicio: string; dataFim: string } {
  const inicio = parse(dataInicio, 'yyyy-MM-dd', new Date());
  const fim = parse(dataFim, 'yyyy-MM-dd', new Date());
  const dias = differenceInCalendarDays(fim, inicio) + 1;
  const fimAnterior = subDays(inicio, 1);

  return {
    dataInicio: format(subDays(fimAnterior, dias - 1), 'yyyy-MM-dd'),
    dataFim: format(fimAnterior, 'yyyy-MM-dd'),
  };
}

// ---- Drill-down de período pelas dimensões de agrupamento temporal ----

/** Dimensões de agrupamento temporal (discriminador de período) presentes em todos os relatórios */
export const PERIODO_DIMENSION_FIELDS = ['data_criacao_dia', 'data_criacao_semana', 'data_criacao_mes'] as const;

/**
 * A partir do valor bruto de uma dimensão de período (dia/semana/mês) de uma linha do relatório,
 * calcula o intervalo de datas correspondente para aplicar como período customizado.
 * Ex: clicar em "Período (semana)" com valor 2026-01-26 retorna o intervalo daquela semana inteira.
 */
export function periodoRangeFromDimensionValue(
  dimensionField: string,
  rawValue: any,
): { dataInicio: string; dataFim: string } | null {
  if (rawValue == null) return null;
  const d = new Date(rawValue);
  if (isNaN(d.getTime())) return null;
  const fmt = (dt: Date) => format(dt, 'yyyy-MM-dd');

  switch (dimensionField) {
    case 'data_criacao_dia':
      return { dataInicio: fmt(d), dataFim: fmt(d) };
    case 'data_criacao_semana':
      return {
        dataInicio: fmt(startOfWeek(d, { weekStartsOn: 0 })),
        dataFim: fmt(endOfWeek(d, { weekStartsOn: 0 })),
      };
    case 'data_criacao_mes':
      return { dataInicio: fmt(startOfMonth(d)), dataFim: fmt(endOfMonth(d)) };
    default:
      return null;
  }
}

// ---- Faixas de qualidade (0-100%), usadas pelo gauge de NPS e por outras
// visualizações que precisam de um esquema de cor consistente pro mesmo tipo de dado ----

export const SCORE_THRESHOLDS = [
  { label: 'Pode melhorar!', value: 25, color: 'var(--tc-color-danger)' },
  { label: 'Regular', value: 50, color: 'var(--tc-color-warning)' },
  { label: 'Bom!', value: 75, color: 'var(--tc-color-info)' },
  { label: 'Excelente!', value: 100, color: 'var(--tc-color-success)' },
];

export const corPorScore = (value: number): string => {
  const ordenados = [...SCORE_THRESHOLDS].sort((a, b) => a.value - b.value);
  return (ordenados.find((t) => value <= t.value) ?? ordenados[ordenados.length - 1]).color;
};

/**
 * Versão suave (pastel) da cor de `corPorScore`, pra usar como preenchimento de
 * área/célula — a cor cheia funciona bem como texto/traço, mas em blocos grandes
 * fica forte demais. `color-mix` deriva do token do tema em vez de duplicar como
 * hexadecimal fixo, então acompanha qualquer ajuste futuro nas cores base.
 */
export const corFundoPastel = (value: number): string =>
  `color-mix(in srgb, ${corPorScore(value)} 25%, white)`;
